// ═══ APPEAL SAVINGS PER BASE, MEASURED FROM A MARKED BASELINE ═══
// (docs/savings-baseline-proposal.md — Philip, 2026-10-08)
//
// A parcel-year can carry more Proposed columns than it has taxable bases: 30-3129-029-0010 / 2023 holds the original
// notice (County, School), the assessor's REVISED notice (County-Adjustment, Public Schools) and a copy of the final
// school value (School-Adjustment). Savings are measured from the REVISED notice wherever one exists — a drop the
// assessor made on their own is not a saving we won.
//
// ⛔ THE BASELINE IS MARKED, NEVER INFERRED. parcel_appeals.savings_baseline_for on a Proposed column names the
// final-level column whose saving it measures. Nothing here reads meaning into a column's NAME ("Adjustment",
// "Public Schools"); a person picked the pairing, and this follows it.
//
// ⛔ ONLY WHEN A MARKER EXISTS. With no marker on the parcel-year this returns null and every caller keeps its
// existing arithmetic — the same figures as before this was built.
//
// Per final column F (the accepted level in reports, Appeal One on the card):
//   baseline_F  = the Proposed column marked "for F", else the Proposed column NAMED F (the existing pairing)
//   reduction_F = baseline_F.taxable − F.taxable
//   savings     = Σ reduction_F × rate_F            — each base at ITS OWN rate, never the combined one
// Proposed columns that are no one's baseline are left out, rather than making the whole sum partial.
// A zero reduction needs no rate. A base that needs a rate and has none blanks the TOTAL (a partial saving is not a
// saving — it is invoiced against) and says why, in words.
import { suggestJurisdictionRates } from './rateSuggestion';
import { ratePercentOf, sameJurisdictionRow } from './appealRows';
import { jurisdictionLabel } from './jurisdictionLabel';

const num = (v) => {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};

export const hasBaselineMarkers = (proposedRows) => (proposedRows || []).some((r) => r && r.savings_baseline_for);

// rate_F, in order: (1) this year's BILL lines grouped to the final columns by taxable value (only when the grouping
// is sure — suggestJurisdictionRates refuses otherwise); (2) the rate saved in the Tax Rate row, under F's own name,
// then under its baseline column's name. → (finalRow, baselineRow) => { pct, source } | null
export function baseRateSource({ billLines, finalRows, savedRates, year }) {
  const fromBill = (billLines && billLines.length)
    ? suggestJurisdictionRates(billLines, finalRows, { year, source: 'bill' }).byJurisdiction : {};
  return (f, b) => {
    const hit = fromBill[f.jurisdiction];
    if (hit) return { pct: hit.unit === 'mills' ? hit.rate / 10 : hit.rate, source: `${year} bill (${hit.lineCount} line${hit.lineCount === 1 ? '' : 's'})` };
    for (const name of [f.jurisdiction, b && b.jurisdiction]) {
      const pct = name ? ratePercentOf((savedRates || {})[name]) : null;
      if (pct != null) return { pct, source: `Tax Rate row (${jurisdictionLabel(name)})` };
    }
    return null;
  };
}

// → null when no marker is set; else
//   { total, bases: [{ final, baselineColumn, baseline, finalValue, reduction, pct, rateSource, savings, note }],
//     headline, missing: [reason…] }
// headline — the base the single-figure Proposed / Final / Reduction columns describe: the one with the LARGEST
// reduction (so Proposed − Final = Reduction still holds on the row); if none is positive, the final level's
// primary column.
export function baselineSavings({ proposedRows, finalRows, rateFor }) {
  if (!hasBaselineMarkers(proposedRows)) return null;
  const finals = (finalRows || []).filter((r) => r && r.jurisdiction);
  const bases = finals.map((f) => {
    const b = (proposedRows || []).find((r) => r && r.savings_baseline_for === f.jurisdiction)
      || sameJurisdictionRow(proposedRows, f.jurisdiction);
    const base = { final: f.jurisdiction, primary: f.is_primary === true, baselineColumn: b ? b.jurisdiction : null,
      baseline: b ? num(b.taxable_total) : null, finalValue: num(f.taxable_total),
      reduction: null, pct: null, rateSource: null, savings: null, note: null };
    if (!b) { base.note = `${jurisdictionLabel(f.jurisdiction)}: no Proposed column is its baseline`; return base; }
    if (base.baseline == null || base.finalValue == null) { base.note = `${jurisdictionLabel(f.jurisdiction)}: a taxable value is missing`; return base; }
    base.reduction = base.baseline - base.finalValue;
    if (base.reduction === 0) { base.savings = 0; return base; }
    const r = rateFor ? rateFor(f, b) : null;
    if (!r) { const name = jurisdictionLabel(f.jurisdiction); base.note = `${name}: enter the ${name} rate in the Tax Rate row`; return base; }
    base.pct = r.pct; base.rateSource = r.source;
    base.savings = Math.round(base.reduction * (r.pct / 100) * 100) / 100;
    return base;
  });
  const missing = bases.filter((x) => x.savings == null).map((x) => x.note);
  const total = bases.length && !missing.length ? Math.round(bases.reduce((a, x) => a + x.savings, 0) * 100) / 100 : null;
  const ranked = bases.filter((x) => x.reduction != null && x.reduction > 0).sort((a, b) => b.reduction - a.reduction);
  const headline = ranked[0] || bases.find((x) => x.primary) || bases[0] || null;
  return { total, bases, headline, missing };
}
